import { supabase } from '../supabaseClient';
import { siteService } from './siteService';

export type ClientContractStatus = 'draft' | 'invited' | 'active' | 'archived';
export type ClientInviteStatus = 'pending' | 'accepted' | 'cancelled' | 'expired';

export interface ClientInviteRow {
  id: string;
  client_contract_id: string;
  email: string;
  full_name: string | null;
  status: ClientInviteStatus;
  expires_at: string;
  last_sent_at: string | null;
  sent_count: number;
  provider_message_id?: string | null;
  accepted_at?: string | null;
  created_at: string;
}

export interface ClientContractRow {
  id: string;
  company_id: string;
  name: string;
  /** ISO 3166-1 alpha-2. New sites for this client start in this country. */
  country_code: string;
  billing_email: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  notes: string | null;
  status: ClientContractStatus;
  invite_sent_at: string | null;
  accepted_at: string | null;
  created_at: string;
  updated_at: string;
  site_count: number;
  latest_invite: ClientInviteRow | null;
}

export interface CreateClientContractParams {
  name: string;
  /** Left out, the database gives a new client the agency's country. */
  countryCode?: string | null;
  billingEmail?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  notes?: string | null;
}

export type UpdateClientContractParams = CreateClientContractParams;

export interface FetchClientContractsOptions {
  includeArchived?: boolean;
}

interface ClientContractLifecycleResponse {
  success: boolean;
  client_contract_id: string;
  status?: ClientContractStatus;
  deleted_site_count?: number;
  deleted_client_user_count?: number;
}

interface InviteEmailResponse {
  success: boolean;
  inviteId: string;
  emailId: string | null;
  email: string;
  expiresAt: string;
}

export interface ContractBaseRow {
  id: string;
  company_id: string;
  name: string;
  country_code: string;
  billing_email: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  notes: string | null;
  status: ClientContractStatus;
  invite_sent_at: string | null;
  accepted_at: string | null;
  created_at: string;
  updated_at: string;
}

const CONTRACT_COLUMNS = `
  id,
  company_id,
  name,
  country_code,
  billing_email,
  contact_name,
  contact_email,
  contact_phone,
  notes,
  status,
  invite_sent_at,
  accepted_at,
  created_at,
  updated_at
`;

interface SiteCountRow {
  id: string;
  client_contract_id: string | null;
}

async function getFunctionErrorMessage(error: unknown): Promise<string | null> {
  const context = (error as { context?: Response | null })?.context;
  if (context) {
    try {
      const payload = await context.clone().json() as { error?: string; message?: string };
      return payload.error ?? payload.message ?? null;
    } catch {
      // Fall back to the SDK error below.
    }
  }

  return error instanceof Error ? error.message : null;
}

export const clientService = {
  /** The agency's own country: what a new client defaults to. */
  async fetchCompanyCountryCode(): Promise<string | null> {
    const companyId = await siteService.getCurrentUserCompanyId();
    const { data, error } = await supabase
      .from('companies')
      .select('country_code')
      .eq('id', companyId)
      .maybeSingle();

    if (error) throw error;
    const code = (data as { country_code?: string | null } | null)?.country_code?.trim();
    return code || null;
  },

  async fetchClientContracts(options: FetchClientContractsOptions = {}): Promise<ClientContractRow[]> {
    const companyId = await siteService.getCurrentUserCompanyId();

    let contractsQuery = supabase
      .from('client_contracts')
      .select(CONTRACT_COLUMNS)
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (!options.includeArchived) {
      contractsQuery = contractsQuery.neq('status', 'archived');
    }

    const [{ data: contracts, error: contractsError }, { data: sites, error: sitesError }, { data: invites, error: invitesError }] = await Promise.all([
      contractsQuery,
      supabase
        .from('sites')
        .select('id, client_contract_id')
        .eq('company_id', companyId),
      supabase
        .from('client_invites')
        .select('id, client_contract_id, email, full_name, status, expires_at, last_sent_at, sent_count, provider_message_id, accepted_at, created_at')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false }),
    ]);

    if (contractsError) throw contractsError;
    if (sitesError) throw sitesError;
    if (invitesError) throw invitesError;

    const siteCountByContractId = new Map<string, number>();
    ((sites ?? []) as SiteCountRow[]).forEach((siteRow) => {
      if (!siteRow.client_contract_id) return;
      siteCountByContractId.set(
        siteRow.client_contract_id,
        (siteCountByContractId.get(siteRow.client_contract_id) ?? 0) + 1,
      );
    });

    const latestInviteByContractId = new Map<string, ClientInviteRow>();
    ((invites ?? []) as ClientInviteRow[]).forEach((inviteRow) => {
      if (!latestInviteByContractId.has(inviteRow.client_contract_id)) {
        latestInviteByContractId.set(inviteRow.client_contract_id, inviteRow);
      }
    });

    return ((contracts ?? []) as ContractBaseRow[]).map((contract) => ({
      ...contract,
      site_count: siteCountByContractId.get(contract.id) ?? 0,
      latest_invite: latestInviteByContractId.get(contract.id) ?? null,
    }));
  },

  async createClientContract(params: CreateClientContractParams): Promise<ClientContractRow> {
    const companyId = await siteService.getCurrentUserCompanyId();
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('client_contracts')
      .insert({
        company_id: companyId,
        created_by: user?.id ?? null,
        name: params.name,
        country_code: params.countryCode ?? null,
        billing_email: params.billingEmail ?? null,
        contact_name: params.contactName ?? null,
        contact_email: params.contactEmail ?? null,
        contact_phone: params.contactPhone ?? null,
        notes: params.notes ?? null,
      })
      .select(CONTRACT_COLUMNS)
      .single();

    if (error) throw error;

    return {
      ...(data as ContractBaseRow),
      site_count: 0,
      latest_invite: null,
    };
  },

  async updateClientContract(clientContractId: string, params: UpdateClientContractParams): Promise<ContractBaseRow> {
    const { data, error } = await supabase
      .from('client_contracts')
      .update({
        name: params.name,
        // Only sent when chosen: a null would reset the client to the agency's country.
        ...(params.countryCode ? { country_code: params.countryCode } : {}),
        billing_email: params.billingEmail ?? null,
        contact_name: params.contactName ?? null,
        contact_email: params.contactEmail ?? null,
        contact_phone: params.contactPhone ?? null,
        notes: params.notes ?? null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', clientContractId)
      .select(CONTRACT_COLUMNS)
      .maybeSingle();

    if (error) throw error;
    if (!data) {
      // RLS returned zero updated rows: the contract is gone or this account lacks update permission.
      throw new Error('The client could not be updated. It may have been removed, or your account may not have permission to edit client contracts.');
    }
    return data as ContractBaseRow;
  },

  async sendClientInvite(clientContractId: string, email?: string | null, fullName?: string | null): Promise<InviteEmailResponse> {
    const { data, error } = await supabase.functions.invoke('send-client-invite', {
      body: {
        action: 'create',
        clientContractId,
        email: email ?? null,
        fullName: fullName ?? null,
      },
    });
    if (error) {
      throw new Error(await getFunctionErrorMessage(error) ?? 'Failed to send invite');
    }
    return data as InviteEmailResponse;
  },

  async resendClientInvite(inviteId: string): Promise<InviteEmailResponse> {
    const { data, error } = await supabase.functions.invoke('send-client-invite', {
      body: {
        action: 'resend',
        inviteId,
      },
    });
    if (error) {
      throw new Error(await getFunctionErrorMessage(error) ?? 'Failed to resend invite');
    }
    return data as InviteEmailResponse;
  },

  async cancelClientInvite(inviteId: string): Promise<void> {
    const { error } = await supabase.rpc('cancel_client_invite', {
      p_invite_id: inviteId,
    });
    if (error) throw error;
  },

  async archiveClientContract(clientContractId: string): Promise<ClientContractLifecycleResponse> {
    const { data, error } = await supabase.rpc('archive_client_contract', {
      p_client_contract_id: clientContractId,
    });
    if (error) throw error;
    return data as ClientContractLifecycleResponse;
  },

  async restoreClientContract(clientContractId: string): Promise<ClientContractLifecycleResponse> {
    const { data, error } = await supabase.rpc('restore_client_contract', {
      p_client_contract_id: clientContractId,
    });
    if (error) throw error;
    return data as ClientContractLifecycleResponse;
  },

  async deleteClientContract(clientContractId: string): Promise<ClientContractLifecycleResponse> {
    const { data, error } = await supabase.rpc('delete_client_contract', {
      p_client_contract_id: clientContractId,
    });
    if (error) throw error;
    return data as ClientContractLifecycleResponse;
  },

};
